import React from 'react';
import { Link } from 'react-router-dom';
import { Mail, ArrowRight, ArrowLeft } from 'lucide-react';
import { motion } from 'framer-motion';
import { blogPosts } from '../data/blogData';
import FooterTrust from '../components/FooterTrust';

const BOT_LINK = "https://t.me/instant_email_bot";

const fadeUp = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.4 } }
};

export default function BlogPage() {
  React.useEffect(() => {
    document.title = "Blog – TelegramEmailBot | Email to Telegram Guides & Tips";
    const desc = document.querySelector('meta[name="description"]');
    if (desc) desc.setAttribute('content', 'Guides, comparisons, and tips for using email inside Telegram. Learn how to receive emails on Telegram, set up team inboxes, and more.');
    const canonicalHref = 'https://telegramemailbot.com/blog';
    const link = document.querySelector('link[rel="canonical"]') || (() => { const l = document.createElement('link'); l.rel = 'canonical'; document.head.appendChild(l); return l; })();
    link.setAttribute('href', canonicalHref);
    const ogUrl = document.querySelector('meta[property="og:url"]');
    if (ogUrl) ogUrl.setAttribute('content', canonicalHref);
  }, []);

  return (
    <div className="min-h-screen bg-white" style={{ fontFamily: "'Manrope', sans-serif" }}>
      <header className="bg-white border-b border-gray-200 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-between h-16">
          <Link to="/" className="flex items-center gap-2" data-testid="blog-logo">
            <Mail className="w-6 h-6 text-[#24A1DE]" />
            <span className="font-bold text-lg text-gray-900" style={{ fontFamily: "'Outfit', sans-serif" }}>Instant Email Bot</span>
          </Link>
          <a href={BOT_LINK} target="_blank" rel="noopener noreferrer" className="text-sm font-semibold bg-[#24A1DE] text-white px-5 py-2 rounded-md hover:bg-[#1d8abf] transition-all" data-testid="blog-header-cta">
            Open Bot
          </a>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-12 sm:py-16">
        <Link to="/" className="inline-flex items-center gap-1 text-sm text-gray-400 hover:text-gray-600 transition-colors mb-8" data-testid="blog-back-home">
          <ArrowLeft className="w-4 h-4" /> Back to Home
        </Link>

        <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-3" style={{ fontFamily: "'Outfit', sans-serif" }} data-testid="blog-page-title">Blog</h1>
        <p className="text-base text-gray-500 mb-12">Guides, comparisons, and tips for email in Telegram.</p>

        <div className="space-y-6">
          {blogPosts.map((post, i) => (
            <motion.div key={post.slug} initial="hidden" animate="visible" variants={fadeUp} transition={{ delay: i * 0.08 }}>
              <Link to={`/blog/${post.slug}`} className="block border border-gray-200 rounded-lg p-6 hover:shadow-sm hover:border-gray-300 transition-all group" data-testid={`blog-list-item-${i}`}>
                <div className="text-xs text-gray-400 mb-2">{post.date} &middot; {post.readTime}</div>
                <h2 className="text-lg font-bold text-gray-900 mb-2 group-hover:text-[#24A1DE] transition-colors" style={{ fontFamily: "'Outfit', sans-serif" }}>{post.title}</h2>
                <p className="text-sm text-gray-500 leading-relaxed mb-3">{post.description}</p>
                <span className="inline-flex items-center gap-1 text-sm font-semibold text-[#24A1DE]">
                  Read article <ArrowRight className="w-4 h-4" />
                </span>
              </Link>
            </motion.div>
          ))}
        </div>
      </main>

      <section className="bg-[#24A1DE] py-12 px-4 text-center" data-testid="blog-cta">
        <h2 className="text-2xl font-bold text-white mb-3" style={{ fontFamily: "'Outfit', sans-serif" }}>Get Your Free Email Now</h2>
        <p className="text-white/80 text-sm mb-6 max-w-md mx-auto">No signup. No credit card. Get an instant email address in Telegram.</p>
        <a href={BOT_LINK} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 bg-white text-[#24A1DE] font-bold px-7 py-3 rounded-md hover:bg-gray-100 transition-all" data-testid="blog-cta-button">
          Get Started ($4.99) <ArrowRight className="w-4 h-4" />
        </a>
      </section>

      <footer className="bg-gray-900 text-gray-300 py-8 px-4">
        <div className="max-w-4xl mx-auto">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-3 text-xs">
            <span>&copy; 2026 TelegramEmailBot. All rights reserved.</span>
            <div className="flex flex-wrap gap-4">
              <Link to="/" className="hover:text-white transition-colors">Home</Link>
              <Link to="/about" className="hover:text-white transition-colors">About</Link>
              <Link to="/contact" className="hover:text-white transition-colors">Contact</Link>
              <Link to="/privacy" className="hover:text-white transition-colors">Privacy</Link>
              <Link to="/terms" className="hover:text-white transition-colors">Terms</Link>
            </div>
          </div>
          <FooterTrust variant="dark" />
        </div>
      </footer>
    </div>
  );
}
